import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  Article,
  ArticleBranchOption,
  ArticleUser,
  UpdateArticleUserRequest,
  articlesApi,
} from '@texas/api/endpoints/articlesApi';
import {
  CustomerUser,
  customerUsersApi,
} from '@texas/api/endpoints/customerUsersApi';
import { User, userApi, UserType } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { userTypeFriendlyName } from '@texas/utils/helpers/roleHelpers';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  article: Article;
  articleUser: ArticleUser | null;
  articleUsers: ArticleUser[];
  initialAssignment: Pick<
    UpdateArticleUserRequest,
    'branchId' | 'userType'
  > | null;
  isOpen: boolean;
  loading: boolean;
  onClose: () => void;
  onRemove: () => void;
  onSave: (userId: number, assignment: UpdateArticleUserRequest) => void;
}

const teamMemberValue = 'team-member';
const customRoleValue = 'custom-role';
type UserFilter = 'all' | 'customer-team';
const articleRoleTypes = [
  UserType.ProductDeveloper,
  UserType.BrandCoordinator,
  UserType.KeyAccountManager,
  UserType.CustomerService,
];

export function ArticleUserModal({
  article,
  articleUser,
  articleUsers,
  initialAssignment,
  isOpen,
  loading,
  onClose,
  onRemove,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const {
    data,
    loading: usersLoading,
    error,
    refetch,
  } = useApiResource(userApi.getAllInternal);
  const {
    data: branches,
    loading: branchesLoading,
    error: branchesError,
    refetch: refetchBranches,
  } = useApiResource(articlesApi.getArticleBranches);
  const {
    data: customerTeam,
    loading: customerTeamLoading,
    error: customerTeamError,
    refetch: refetchCustomerTeam,
  } = useApiResource(customerUsersApi.getUsers);
  const [userId, setUserId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [role, setRole] = useState(teamMemberValue);
  const [customRole, setCustomRole] = useState('');
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    void refetch();
    void refetchBranches(article.id);
    if (!articleUser) void refetchCustomerTeam(article.customerId);
    setUserId(articleUser ? String(articleUser.userId) : '');
    setBranchId(
      articleUser?.branchId
        ? String(articleUser.branchId)
        : initialAssignment?.branchId
        ? String(initialAssignment.branchId)
        : '',
    );
    setRole(
      articleUser?.userType
        ? String(ensureEnumNumber(UserType, articleUser.userType))
        : articleUser?.customUserType
        ? customRoleValue
        : initialAssignment?.userType
        ? String(initialAssignment.userType)
        : teamMemberValue,
    );
    setCustomRole(articleUser?.customUserType ?? '');
    setUserSearch('');
  }, [
    article.customerId,
    article.id,
    articleUser,
    initialAssignment,
    isOpen,
    refetch,
    refetchBranches,
    refetchCustomerTeam,
  ]);

  const users = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  );
  const isCustomRole = role === customRoleValue;
  const selectedUserType =
    role === teamMemberValue || role === customRoleValue
      ? null
      : (Number(role) as UserType);
  const selectedBranchId = branchId ? Number(branchId) : null;
  const isAlreadyAssigned = (candidateUserId: number) =>
    articleUsers.some(
      (assignment) =>
        assignment.userId === candidateUserId &&
        assignment.branchId === selectedBranchId &&
        normalizeUserType(assignment.userType) === selectedUserType &&
        (isCustomRole
          ? assignment.customUserType === customRole.trim()
          : assignment.customUserType === null),
    );
  const selectedUserAlreadyAssigned = userId
    ? isAlreadyAssigned(Number(userId))
    : false;
  const canSave =
    userId !== '' &&
    !selectedUserAlreadyAssigned &&
    (!isCustomRole || customRole.trim().length > 0);
  const selectedUser = users.find((user) => user.id === Number(userId));
  const selectedBranch = (branches ?? []).find(
    (branch) => branch.id === selectedBranchId,
  );
  const selectedRoleLabel = selectedUserType
    ? userTypeFriendlyName(selectedUserType, t)
    : isCustomRole
    ? customRole
    : t('article.management.teamMember');

  const save = () => {
    if (!canSave) return;

    onSave(Number(userId), {
      branchId: selectedBranchId,
      userType: selectedUserType,
      customUserType: isCustomRole ? customRole.trim() : null,
    });
  };

  return (
    <Modal
      isCentered={true}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(
            articleUser
              ? 'article.management.editPerson'
              : 'article.management.addPerson',
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="articleManagement.mutedText" fontSize="sm" mb={6}>
            {t('article.management.personModalDescription')}
          </Text>
          {(usersLoading || customerTeamLoading) && (
            <Flex justify="center" py={4}>
              <Spinner />
            </Flex>
          )}
          {error && <ErrorDetails error={error} />}
          {branchesError && <ErrorDetails error={branchesError} />}
          {customerTeamError && <ErrorDetails error={customerTeamError} />}

          <Box>
            <Text
              color="articleManagement.mutedText"
              fontFamily="barlow"
              fontSize="12px"
              fontWeight="semibold"
              letterSpacing="0.08em"
              mb={3}
              textTransform="uppercase"
            >
              {t('article.management.assignment')}
            </Text>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
              <FormControl>
                <FormLabel fontSize="13px">
                  {t('article.management.articleRole')}
                </FormLabel>
                <Select
                  variant="articleManagement"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  <option value={teamMemberValue}>
                    {t('article.management.teamMember')}
                  </option>
                  {articleRoleTypes.map((userType) => (
                    <option key={userType} value={userType}>
                      {userTypeFriendlyName(userType, t)}
                    </option>
                  ))}
                  <option value={customRoleValue}>
                    {t('article.management.customRole')}
                  </option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="13px">
                  {t('article.management.locationScope')}
                </FormLabel>
                <Select
                  isDisabled={branchesLoading}
                  variant="articleManagement"
                  value={branchId}
                  onChange={(event) => setBranchId(event.target.value)}
                >
                  <option value="">
                    {t('article.management.allLocations')}
                  </option>
                  {(branches ?? []).map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} · {branch.identifier}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isCustomRole && (
              <FormControl isRequired={true} mt={3}>
                <FormLabel fontSize="13px">
                  {t('article.management.customRole')}
                </FormLabel>
                <Input
                  maxLength={100}
                  variant="texas"
                  value={customRole}
                  onChange={(event) => setCustomRole(event.target.value)}
                />
              </FormControl>
            )}
          </Box>

          {articleUser ? (
            <FormControl
              borderTop="1px solid"
              borderColor="articleManagement.divider"
              mt={6}
              pt={5}
            >
              <FormLabel>{t('article.management.person')}</FormLabel>
              <Input
                isReadOnly={true}
                variant="texas"
                value={`${articleUser.name} · ${articleUser.email}`}
              />
            </FormControl>
          ) : (
            <UserPicker
              alreadyAssigned={isAlreadyAssigned}
              branches={branches ?? []}
              customerTeam={customerTeam ?? []}
              role={selectedUserType}
              search={userSearch}
              selectedBranchId={selectedBranchId}
              selectedUserId={userId ? Number(userId) : null}
              users={users}
              onSearch={setUserSearch}
              onSelect={(id) => setUserId(String(id))}
            />
          )}
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Flex align="flex-start" direction="column" gap={2}>
            <Text variant="note" _light={{ color: 'yellow.700' }}>
              {t('article.management.requiredFieldsDescription')}
            </Text>
            {articleUser ? (
              <Button colorScheme="red" variant="outline" onClick={onRemove}>
                {t('article.management.removePerson')}
              </Button>
            ) : (
              <SelectedPersonSummary
                branch={selectedBranch?.identifier ?? null}
                name={selectedUser?.name ?? null}
                role={selectedRoleLabel}
              />
            )}
          </Flex>
          <Flex gap={2.5} ml="auto">
            <Button onClick={onClose}>{t('general.cancel')}</Button>
            <Button
              isDisabled={!canSave}
              isLoading={loading}
              variant="texas-solid"
              onClick={save}
            >
              {t(articleUser ? 'general.save' : 'article.management.addPerson')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function UserPicker({
  alreadyAssigned,
  branches,
  customerTeam,
  role,
  search,
  selectedBranchId,
  selectedUserId,
  users,
  onSearch,
  onSelect,
}: {
  alreadyAssigned: (userId: number) => boolean;
  branches: ArticleBranchOption[];
  customerTeam: CustomerUser[];
  role: UserType | null;
  search: string;
  selectedBranchId: number | null;
  selectedUserId: number | null;
  users: User[];
  onSearch: (search: string) => void;
  onSelect: (userId: number) => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<UserFilter>('customer-team');
  const normalizedSearch = search.trim().toLowerCase();
  const teamUserIds = new Set(customerTeam.map((user) => user.userId));
  const filteredUsers = users.filter(
    (user) =>
      !user.archived &&
      (filter === 'all' || teamUserIds.has(user.id)) &&
      (normalizedSearch.length === 0 ||
        `${user.name} ${user.email}`.toLowerCase().includes(normalizedSearch)),
  );
  const recommendedUsers = filteredUsers.filter(
    (user) =>
      teamUserIds.has(user.id) &&
      (selectedBranchId === null || user.branchId === selectedBranchId) &&
      normalizeUserType(user.userType) === role &&
      !alreadyAssigned(user.id),
  );
  const otherUsers = filteredUsers.filter(
    (user) =>
      !recommendedUsers.some((recommended) => recommended.id === user.id),
  );
  const selectedBranch = branches.find(
    (branch) => branch.id === selectedBranchId,
  );
  const roleLabel = role
    ? userTypeFriendlyName(role, t)
    : t('article.management.teamMember');

  return (
    <Box
      borderTop="1px solid"
      borderColor="articleManagement.divider"
      mt={6}
      pt={5}
    >
      <Flex align="flex-end" justify="space-between" mb={3}>
        <Box>
          <Text fontFamily="barlow" fontSize="16px" fontWeight="semibold">
            {t('article.management.choosePerson')}{' '}
            <Text as="span" color="articleManagement.accent">
              *
            </Text>
          </Text>
          <Text color="articleManagement.mutedText" fontSize="13px" mt={0.5}>
            {t('article.management.recommendationDescription')}
          </Text>
        </Box>
        <Text color="articleManagement.mutedText" fontSize="12px">
          {t('article.management.recommendedCount', {
            count: recommendedUsers.length,
          })}
        </Text>
      </Flex>
      <Flex gap={2.5} mb={3.5}>
        <InputGroup>
          <InputLeftElement h="42px" pointerEvents="none">
            <Icons.Search color="articleManagement.mutedText" boxSize={4} />
          </InputLeftElement>
          <Input
            h="42px"
            placeholder={t('article.management.searchUsersByNameOrEmail')}
            variant="texas"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
          />
        </InputGroup>
        <Select
          bg="articleManagement.subtleSurface"
          flex="0 0 206px"
          variant="articleManagement"
          value={filter}
          onChange={(event) => setFilter(event.target.value as UserFilter)}
        >
          <option value="customer-team">
            {t('article.management.customerTeamMember')}
          </option>
          <option value="all">{t('article.productDevelopers.allUsers')}</option>
        </Select>
      </Flex>

      <Box
        border="1px solid"
        borderColor="articleManagement.border"
        maxH="418px"
        overflowY="auto"
      >
        {recommendedUsers.length > 0 && (
          <UserSection
            alreadyAssigned={alreadyAssigned}
            branches={branches}
            customerTeamUserIds={teamUserIds}
            meta={[
              t('article.management.customerTeamMember'),
              selectedBranch?.identifier,
              roleLabel,
            ]
              .filter(Boolean)
              .join(' · ')}
            title={t('article.management.recommendedMatches')}
            users={recommendedUsers}
            selectedUserId={selectedUserId}
            onSelect={onSelect}
          />
        )}
        <UserSection
          alreadyAssigned={alreadyAssigned}
          branches={branches}
          customerTeamUserIds={teamUserIds}
          title={t(
            filter === 'customer-team'
              ? 'article.management.otherCustomerTeamMembers'
              : 'article.management.otherUsers',
          )}
          users={otherUsers}
          selectedUserId={selectedUserId}
          onSelect={onSelect}
        />
      </Box>
    </Box>
  );
}

function UserSection({
  alreadyAssigned,
  branches,
  customerTeamUserIds,
  meta,
  selectedUserId,
  title,
  users,
  onSelect,
}: {
  alreadyAssigned: (userId: number) => boolean;
  branches: ArticleBranchOption[];
  customerTeamUserIds: Set<number>;
  meta?: string;
  selectedUserId: number | null;
  title: string;
  users: User[];
  onSelect: (userId: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box>
      <Flex
        align="center"
        bg={
          meta ? 'articleManagement.subtleSurface' : 'articleManagement.surface'
        }
        borderBottom="1px solid"
        borderColor="articleManagement.border"
        justify="space-between"
        minH={meta ? '42px' : '38px'}
        px={4}
        py={2.5}
      >
        <Flex align="center" gap={2}>
          {meta && <Icons.ThumbUp boxSize={4} />}
          <Text
            color={
              meta ? 'articleManagement.text' : 'articleManagement.mutedText'
            }
            fontFamily="barlow"
            fontSize="12px"
            fontWeight="semibold"
            letterSpacing="0.07em"
            textTransform="uppercase"
          >
            {title}
          </Text>
        </Flex>
        <Text color="articleManagement.mutedText" fontSize="12px">
          {meta ?? t('article.management.personCount', { count: users.length })}
        </Text>
      </Flex>
      <Flex direction="column">
        {users.map((user) => {
          const branch = branches.find((item) => item.id === user.branchId);
          const assigned = alreadyAssigned(user.id);
          const selected = selectedUserId === user.id;

          return (
            <Box
              key={user.id}
              as="button"
              aria-pressed={selected}
              bg={
                selected
                  ? 'articleManagement.selectedSurface'
                  : 'articleManagement.surface'
              }
              borderBottom="1px solid"
              borderBottomColor="articleManagement.divider"
              borderLeft="3px solid"
              borderLeftColor={
                selected ? 'articleManagement.accent' : 'transparent'
              }
              borderRadius={0}
              color="articleManagement.text"
              cursor={assigned ? 'not-allowed' : 'pointer'}
              disabled={assigned}
              minH="76px"
              px={4}
              py={3}
              textAlign="left"
              w="100%"
              _disabled={{
                color: 'articleManagement.text',
                cursor: 'not-allowed',
                opacity: 0.5,
              }}
              _hover={{
                bg: selected
                  ? 'articleManagement.selectedSurface'
                  : 'articleManagement.subtleSurface',
              }}
              onClick={() => onSelect(user.id)}
            >
              <Grid
                alignItems="center"
                gap={3}
                minW="700px"
                templateColumns="16px 34px minmax(180px, 1fr) 100px 120px 130px"
                textAlign="left"
              >
                <Flex
                  align="center"
                  border="1.5px solid"
                  borderColor={
                    selected
                      ? 'articleManagement.accent'
                      : 'articleManagement.inputBorderHover'
                  }
                  borderRadius="full"
                  boxSize="16px"
                  justify="center"
                >
                  {selected && (
                    <Box
                      bg="articleManagement.accent"
                      borderRadius="full"
                      boxSize="8px"
                    />
                  )}
                </Flex>
                <Flex
                  align="center"
                  bg="articleManagement.text"
                  borderRadius="full"
                  color="white"
                  fontSize="11px"
                  fontWeight="semibold"
                  h="34px"
                  justify="center"
                  w="34px"
                >
                  {initials(user.name)}
                </Flex>
                <Box minW={0}>
                  <Text fontSize="14px" fontWeight="semibold" noOfLines={1}>
                    {user.name}
                  </Text>
                  <Text
                    color="articleManagement.mutedText"
                    fontSize="12px"
                    noOfLines={1}
                  >
                    {user.email}
                  </Text>
                </Box>
                <Text
                  color="articleManagement.text"
                  fontSize="12px"
                  noOfLines={2}
                >
                  {branch?.name ?? t('article.management.allLocations')}
                </Text>
                <Text
                  color="articleManagement.text"
                  fontSize="12px"
                  noOfLines={2}
                >
                  {user.userType
                    ? userTypeFriendlyName(
                        ensureEnumNumber(UserType, user.userType),
                        t,
                      )
                    : t('article.management.teamMember')}
                </Text>
                <Flex justify="flex-end">
                  {assigned ? (
                    <Badge>{t('article.management.alreadyAssigned')}</Badge>
                  ) : (
                    customerTeamUserIds.has(user.id) && (
                      <Badge
                        bg="status.successBackground"
                        color="status.success"
                      >
                        {t('article.management.teamBadge')}
                      </Badge>
                    )
                  )}
                </Flex>
              </Grid>
            </Box>
          );
        })}
        {users.length === 0 && (
          <Text color="articleManagement.mutedText" fontSize="sm">
            {t('article.management.noUsersFound')}
          </Text>
        )}
      </Flex>
    </Box>
  );
}

function SelectedPersonSummary({
  branch,
  name,
  role,
}: {
  branch: string | null;
  name: string | null;
  role: string;
}) {
  const { t } = useTranslation();

  if (!name) return <Box />;

  return (
    <Flex align="center" gap={2.5} minW={0}>
      <Box
        bg="articleManagement.accent"
        borderRadius="full"
        boxSize="7px"
        flexShrink={0}
      />
      <Box minW={0}>
        <Text color="articleManagement.mutedText" fontSize="12px">
          {t('article.management.selectedPerson')}
        </Text>
        <Text fontSize="14px" fontWeight="semibold" noOfLines={1}>
          {[name, branch ?? t('article.management.allLocations'), role].join(
            ' · ',
          )}
        </Text>
      </Box>
    </Flex>
  );
}

function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function normalizeUserType(userType: UserType | null) {
  return userType ? ensureEnumNumber(UserType, userType) : null;
}
